import { Star } from "@gigpro/ui/Icons";
import { cn } from "@gigpro/ui/tailwind/cn";
import { Tooltip } from "@gigpro/ui/Tooltip";
import type { RatingStats } from "@sidegigllc/api/client";
import React from "react";

export type RatingSummaryStat = Omit<RatingStats, "avg_rating"> & { avg_rating: number | null };

const hasPaidRating = (rating: number | null | undefined): rating is number => rating != null && rating > 0;

interface SingleRatingDisplayProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: React.Ref<HTMLDivElement>;
  title?: string;
  rating: number | null;
  reviewCount?: number;
}

const SingleRatingDisplay = ({ ref, title, rating, reviewCount, className, ...rest }: SingleRatingDisplayProps) => {
  const hasRating = hasPaidRating(rating);
  return (
    <div ref={ref} className={cn("flex w-fit flex-col text-normal", className)} {...rest}>
      {title && <p className="text-[length:inherit] font-semibold">{title}</p>}
      <p className="flex flex-row items-center text-[length:inherit] font-semibold">
        {hasRating && <Star className="mr-0.5 size-4 shrink-0 text-warning" />}
        {hasRating ? Number(rating).toFixed(1) : "—"}&nbsp;
        {reviewCount != null && (
          <span className="font-regular">
            (<strong>{reviewCount}</strong> reviews)
          </span>
        )}
      </p>
    </div>
  );
};

interface RatingDisplayProps extends React.HTMLAttributes<HTMLDivElement> {
  rating?: number | null;
  ratings?: ReadonlyArray<RatingSummaryStat>;
  countsEnabled?: boolean;
}

export const RatingDisplay: React.FC<RatingDisplayProps> = ({ rating, ratings, countsEnabled = false, ...rest }) => {
  let overallRating: RatingSummaryStat = {
    name: "overall",
    label: "Overall",
    is_overall_type: true,
    rank: 0,
    avg_rating: rating ?? null,
    rating_count: 0,
  };
  const subRatings: Array<RatingSummaryStat> = [];
  for (const type of ratings ?? []) {
    if (type.is_overall_type) overallRating = type;
    else subRatings.push(type);
  }

  const overallRatingDisplay = (
    <SingleRatingDisplay
      rating={overallRating.avg_rating}
      reviewCount={countsEnabled ? overallRating.rating_count : undefined}
      {...rest}
    />
  );

  return subRatings.length && hasPaidRating(overallRating.avg_rating) ? (
    <Tooltip
      content={
        <>
          <SingleRatingDisplay
            title="Performance"
            rating={overallRating.avg_rating}
            reviewCount={countsEnabled ? overallRating.rating_count : undefined}
          />
          <div className="mt-2 grid grid-cols-2 gap-2">
            {subRatings.map(({ name, label, avg_rating, rating_count }) => (
              <SingleRatingDisplay
                key={name}
                title={label}
                rating={rating_count > 0 && hasPaidRating(avg_rating) ? avg_rating : null}
                reviewCount={countsEnabled ? rating_count : undefined}
                className="text-small"
              />
            ))}
          </div>
        </>
      }
    >
      {overallRatingDisplay}
    </Tooltip>
  ) : (
    overallRatingDisplay
  );
};
